<template>
  <div>
    <div class="page-wrapper-table">
      <div class="page-wrapper-table-header">
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <organization-selector
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
          @organizationChanged="
            (organizationId) => (selectedOrganization = organizationId)
          "
        />

        <base-input>
          <el-select
            clearable
            :placeholder="$t('TAXES.TYPE')"
            v-model="selectedType"
          >
            <el-option
              v-for="(taxe, val) in taxeTypes"
              :key="val"
              :value="val"
              :label="`${val ? $t('TAXES.' + val) : ''}`"
            />
          </el-select>
        </base-input>

        <base-input>
          <el-select
            clearable
            :placeholder="$t('TAXES.TRANSACTION')"
            v-model="selectedTransactionType"
          >
            <el-option
              v-for="(label, val) in taxeTransactions"
              :key="val"
              :value="val"
              :label="`${val ? $t('TAXES.' + val) : ''}`"
            />
          </el-select>
        </base-input>

        <base-input>
          <el-select
            clearable
            :placeholder="$t('TAXES.ACTIVE')"
            v-model="selectedStatus"
          >
            <el-option :value="true" :label="$t('TAXES.ACTIVE')" />
            <el-option :value="false" :label="$t('TAXES.DEACTIVATED')" />
          </el-select>
        </base-input>

        <base-input
          v-model="query"
          type="search"
          prepend-icon="far fa-search"
          :placeholder="$t('COMMON.SEARCH')"
          clearable
        />
        <base-button
          class="btn base-button notification bg-dark btn-icon btn-fab btn-info btn-sm mb-4"
          icon
          size="sm"
          @click="resetFilters"
        >
          <span class="btn-inner--icon">
            <i class="fa fa-undo"></i>
          </span>
        </base-button>
      </div>
      <div class="page-wrapper-table-body">
        <div class="page-wrapper-table-body-inner">
          <el-table
            class="align-items-center table-flush"
            header-row-class-name="thead-light"
            :data="taxes"
            @sort-change="sortChange"
          >
            <div class="table-loading" slot="empty" v-if="loading">
              <img src="/img/loading.gif" />
            </div>

            <el-table-column
              :label="$t('COMMON.ORGANIZATION')"
              min-width="200"
              v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
            >
              <template v-slot="{ row }">
                <span>{{ row.organization ? row.organization.name : "" }}</span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('COMMON.NAME')"
              prop="name"
              sortable="custom"
              min-width="250"
            >
              <template v-slot="{ row }">
                <span>
                  {{ row.name ?? "" }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('TAXES.DENOMINATION')" min-width="150">
              <template v-slot="{ row }">
                {{ row.label }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('TAXES.VALUE')" min-width="200">
              <template v-slot="{ row }">
                <span v-if="row.calculation_type == TYPE_POURCENTAGE"
                  >{{ row.value }} %</span
                >
                <span v-else-if="row.calculation_type == TYPE_MONTANT_FIXE">{{
                  $formatCurrency(row.value)
                }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('TAXES.ACTIVE')" min-width="100">
              <template v-slot="{ row }">
                <badge v-if="row.active" type="success">
                  {{ $t("TAXES.ACTIVE") }}
                </badge>
                <badge v-else type="secondary">
                  {{ $t("TAXES.DEACTIVATED") }}
                </badge>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('TAXES.CREATED_AT')"
              prop="created_at"
              sortable="custom"
              min-width="220"
            >
              <template v-slot="{ row }">
                {{ $timeZoneDateFormat(row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column fixed="right" min-width="120">
              <div slot-scope="{ row }" class="table-actions">
                <el-tooltip
                  :content="$t('COMMON.VIEW')"
                  placement="top"
                  v-if="$currentUserCan($permissions.PERM_VIEW_TAXES)"
                >
                  <a
                    type="text"
                    @click="viewTax(row)"
                    class="table-action"
                    data-toggle="tooltip"
                    style="cursor: pointer"
                  >
                    <i class="fal fa-expand-alt"></i>
                  </a>
                </el-tooltip>
                <el-tooltip
                  :content="$t('COMMON.EDIT')"
                  placement="top"
                  v-if="$currentUserCan($permissions.PERM_EDIT_TAXES)"
                >
                  <a
                    type="text"
                    @click="editTax(row)"
                    class="table-action table-action-delete"
                    data-toggle="tooltip"
                    style="cursor: pointer"
                  >
                    <i class="fal fa-edit"></i>
                  </a>
                </el-tooltip>
                <el-tooltip
                  :content="$t('COMMON.DELETE')"
                  placement="top"
                  v-if="$currentUserCan($permissions.PERM_DELETE_TAXES)"
                >
                  <a
                    type="text"
                    @click="deleteTax(row.id)"
                    class="table-action table-action-delete"
                    data-toggle="tooltip"
                    style="cursor: pointer"
                  >
                    <i class="fal fa-trash-alt"></i>
                  </a>
                </el-tooltip>
              </div>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div slot="footer" class="page-wrapper-table-footer">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}
          <span v-if="selectedRows.length">
            &nbsp; &nbsp;
            {{ $t("COMMON.X_LINES_SELECTED", { count: selectedRows.length }) }}
          </span>
        </p>
        <base-pagination
          class="pagination-no-border"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
        />
      </div>
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Option,
  Select,
  Table,
  TableColumn,
  Tooltip,
  Tag,
} from "element-ui";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { BasePagination } from "@/components";
import {
  TAXE_TYPES,
  TAXE_TRANSACTIONS_TYPES,
  TYPE_POURCENTAGE,
  TYPE_MONTANT_FIXE,
} from "@/constants/taxes";
import OrganizationSelector from "@/components/OrganizationSelector.vue";

export default {
  name: "tax-list-table",

  components: {
    BasePagination,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    [Tag.name]: Tag,
    OrganizationSelector,
  },

  mixins: [requestErrorMixin],

  data() {
    return {
      query: null,
      selectedRows: [],
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      taxes: [],
      loading: true,
      selectedType: null,
      selectedOrganization: null,
      selectedTransactionType: null,
      selectedStatus: null,
      taxeTypes: TAXE_TYPES,
      taxeTransactions: TAXE_TRANSACTIONS_TYPES,
      TYPE_POURCENTAGE: TYPE_POURCENTAGE,
      TYPE_MONTANT_FIXE: TYPE_MONTANT_FIXE,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    selectedType: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedTransactionType: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
  },

  methods: {
    resetFilters() {
      this.query = null;
      this.selectedType = null;
      this.selectedOrganization = null;
      this.selectedTransactionType = null;
      this.selectedStatus = null;
    },

    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList() {
      try {
        this.loading = true;
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: this.query } : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
        };

        if (this.selectedType) {
          params.filter.calculation_type = this.selectedType;
        }

        if (this.selectedOrganization) {
          params.filter.organization = this.selectedOrganization;
        }

        if (this.selectedTransactionType) {
          params.filter.tax_type = this.selectedTransactionType;
        }

        if (this.selectedStatus !== null && this.selectedStatus !== undefined) {
          params.filter.active = this.selectedStatus;
        }

        await this.$store.dispatch("taxes/list", params);
        this.taxes = this.$store.getters["taxes/list"];
        this.total = this.$store.getters["taxes/listTotal"];
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    editTax(tax) {
      this.$emit("onEditTax", tax);
    },

    async deleteTax(id) {
      const confirmation = await swal.fire({
        text: this.$t("TAXES.DELETE_THIS_TAX"),
        type: "question",
        customClass: {
          popup: "delete-popup",
        },
        buttonsStyling: false,
        showCancelButton: true,
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonText:
          this.$t("COMMON.YES") + ", " + this.$t("COMMON.DELETE"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("taxes/destroy", id);
          await this.getList();
          this.$notify({
            type: "success",
            message: this.$t("TAXES.TAX_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    viewTax(tax) {
      this.$emit("onViewTax", tax);
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
